export default function WhatsAppAIModule() {
  return (
    <section id="whatsapp-ai" className="py-24 px-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: WhatsApp chat mockup */}
          <div className="order-2 lg:order-1">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden max-w-sm mx-auto">
              {/* WhatsApp header */}
              <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3">
                <div className="w-8 h-8 bg-teal-300 rounded-full flex items-center justify-center">
                  <i className="ri-plane-line text-[#075E54] text-sm" />
                </div>
                <div>
                  <p className="text-white text-xs font-semibold">TreWaz AI — Your Agency</p>
                  <p className="text-teal-200 text-[10px]">AI Assistant • Online</p>
                </div>
              </div>

              {/* Chat body */}
              <div className="bg-[#ECE5DD] px-3 py-4 space-y-2 min-h-[280px]">
                {/* Customer message */}
                <div className="flex justify-end">
                  <div className="bg-[#DCF8C6] rounded-lg rounded-tr-none px-3 py-2 max-w-[75%]">
                    <p className="text-xs text-gray-800">Hi! I want a 7-day Bali trip for 2 in April, budget ~$3,000</p>
                    <p className="text-[10px] text-gray-400 text-right mt-0.5">10:24 AM ✓✓</p>
                  </div>
                </div>

                {/* AI reply */}
                <div className="flex justify-start">
                  <div className="bg-white rounded-lg rounded-tl-none px-3 py-2 max-w-[80%]">
                    <p className="text-xs text-gray-800 leading-relaxed">
                      Great choice! 🌴 Bali in April is perfect. I have 3 packages that fit your budget perfectly:
                    </p>
                    <div className="mt-2 space-y-1.5">
                      {[
                        { name: 'Romantic Escape', price: '$2,750' },
                        { name: 'Adventure & Culture', price: '$2,950' },
                        { name: 'Luxury Retreat', price: '$3,200' },
                      ].map((pkg) => (
                        <div key={pkg.name} className="flex items-center justify-between bg-teal-50 rounded-md px-2 py-1">
                          <span className="text-[10px] font-medium text-teal-800">{pkg.name}</span>
                          <span className="text-[10px] text-teal-600">{pkg.price}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-800 mt-2">Which one interests you? I can send a full itinerary 📋</p>
                    <p className="text-[10px] text-gray-400 text-right mt-0.5">10:24 AM</p>
                  </div>
                </div>

                {/* Customer again */}
                <div className="flex justify-end">
                  <div className="bg-[#DCF8C6] rounded-lg rounded-tr-none px-3 py-2 max-w-[75%]">
                    <p className="text-xs text-gray-800">The Romantic Escape looks great!</p>
                    <p className="text-[10px] text-gray-400 text-right mt-0.5">10:26 AM ✓✓</p>
                  </div>
                </div>

                {/* AI confirmation */}
                <div className="flex justify-start">
                  <div className="bg-white rounded-lg rounded-tl-none px-3 py-2 max-w-[80%]">
                    <p className="text-xs text-gray-800">Perfect! I\'ve noted your preference. Your travel agent will prepare a detailed itinerary and quote shortly. 📝</p>
                    <div className="mt-1.5 bg-emerald-50 border border-emerald-100 rounded px-2 py-1 text-[10px] text-emerald-700">
                      ✅ Lead qualified • Itinerary requested • Agent notified
                    </div>
                    <p className="text-[10px] text-gray-400 text-right mt-0.5">10:26 AM</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div className="order-1 lg:order-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg">
                <i className="ri-whatsapp-line text-teal-500" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest text-teal-600">Module 02</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 leading-tight tracking-tight mb-4">
              WhatsApp AI That<br />
              <span className="bg-gradient-to-r from-teal-500 to-emerald-500 bg-clip-text text-transparent">Talks to Your Customers</span>
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-6">
              Your AI chatbot handles the entire WhatsApp conversation — answering enquiries, sharing package options, qualifying budget and dates, and keeping customers warm until your agent is ready. Available 24/7.
            </p>
            <ul className="flex flex-col gap-4">
              {[
                { icon: 'ri-24-hours-line', title: '24/7 instant responses', desc: 'No customer waits. The AI replies within seconds, even outside office hours or on weekends.' },
                { icon: 'ri-chat-smile-3-line', title: 'Natural conversation flow', desc: 'Understands travel queries in plain language. Suggests packages, asks clarifying questions, handles objections naturally.' },
                { icon: 'ri-user-star-line', title: 'Lead qualification on the fly', desc: 'AI captures destination, travel dates, number of travellers, and budget — so your agent gets a fully qualified lead.' },
                { icon: 'ri-arrow-right-circle-line', title: 'Smooth agent handoff', desc: 'When a lead is ready, the AI flags it to your team with full context. Agent steps in — no repeat questions needed.' },
              ].map((item) => (
                <li key={item.title} className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg shrink-0 mt-0.5">
                    <i className={`${item.icon} text-teal-600 text-sm`} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-0.5">{item.title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
